const versionReloadTargetKey = "dc_version_reload_target";
const defaultCheckIntervalMs = 5 * 60 * 1000;

type VersionRecoveryOptions = {
  fetcher?: typeof fetch;
  reload?: () => void;
  checkIntervalMs?: number;
};

export function appVersionFromHtml(html: string): string | null {
  const match = html.match(
    /<meta\s+name=["']deploycompare-app-version["']\s+content=["']([^"']+)["']/i
  );
  const version = match?.[1]?.trim();
  return version && !version.startsWith("%VITE_") ? version : null;
}

function readReloadTarget(): string | null {
  try {
    return sessionStorage.getItem(versionReloadTargetKey);
  } catch {
    return null;
  }
}

function writeReloadTarget(version: string): void {
  try {
    sessionStorage.setItem(versionReloadTargetKey, version);
  } catch {
    // The version comparison is still useful when storage is unavailable.
  }
}

function clearReloadTarget(): void {
  try {
    sessionStorage.removeItem(versionReloadTargetKey);
  } catch {
    // ignore — nothing to clear if storage is unavailable
  }
}

/**
 * Detect a newer frontend before stale lazy chunks fail. The reload event is
 * cancelable so a future surface with unsaved work can defer the refresh.
 */
export function installAppVersionRecovery(
  currentVersion: string | undefined,
  options: VersionRecoveryOptions = {}
): () => void {
  const version = currentVersion?.trim();
  const fetcher = options.fetcher ?? window.fetch.bind(window);
  const reload = options.reload ?? (() => window.location.reload());
  if (!version || version.startsWith("%VITE_")) return () => undefined;

  let checkInFlight = false;
  let disposed = false;

  const check = async () => {
    if (checkInFlight || disposed) return;
    checkInFlight = true;
    try {
      const response = await fetcher(`/index.html?dc-version=${Date.now()}`, {
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "text/html" },
      });
      if (!response.ok || disposed) return;
      const availableVersion = appVersionFromHtml(await response.text());
      if (!availableVersion || disposed) return;
      if (availableVersion === version) {
        clearReloadTarget();
        return;
      }
      // Avoid a loop if CDN propagation makes a normal navigation briefly
      // serve the old HTML after the no-store probe has seen the new release.
      if (readReloadTarget() === availableVersion) return;
      const shouldReload = window.dispatchEvent(
        new Event("deploycompare:version-reload", { cancelable: true })
      );
      if (!shouldReload) return;
      writeReloadTarget(availableVersion);
      reload();
    } catch {
      // Version checks are best-effort and must never interrupt the app.
    } finally {
      checkInFlight = false;
    }
  };

  const checkWhenVisible = () => {
    if (document.visibilityState === "visible") void check();
  };
  const checkOnFocus = () => void check();

  // Check at startup, whenever the user returns to a long-lived tab, and
  // periodically while it remains active.
  void check();
  document.addEventListener("visibilitychange", checkWhenVisible);
  window.addEventListener("focus", checkOnFocus);
  const intervalId = window.setInterval(
    checkWhenVisible,
    options.checkIntervalMs ?? defaultCheckIntervalMs
  );

  return () => {
    disposed = true;
    document.removeEventListener("visibilitychange", checkWhenVisible);
    window.removeEventListener("focus", checkOnFocus);
    window.clearInterval(intervalId);
  };
}
